import { computed, reactive, ref, watch } from '@nuxtjs/composition-api';
import { CUSTOMER_TYPE } from '~/constants/CheckoutFlow';
import useTlmCart from '~/composable/useTlmCart';

export default function useCustomerInfo (appContext) {
  const route = appContext.context.route;
  const code = computed(() => route.query.code)
  const { cartHasHardware } = useTlmCart(appContext)
  const permissionsAccepted = ref(null)
  const permissionHeading = 'Vil du have nyheder, gode råd og tilbud?'
  const permissionText1 = 'Ja tak, jeg giver samtykke til, at Telmore (brand af TDC brands A/S) må kontakte mig via e-mail, SMS, telefon samt push-beskeder fra Telmores apps med markedsføring og aftaleindgåelse om Telmores produktsortiment, herunder, internet, telefoni og underholdning.'
  const permissionText2 = 'Samtykket dækker også, at Markedsføringen tilpasses ud fra besøgs- og søgehistorik hos Telmore, købshistorik, trafikdata, kontaktoplysninger, køn og alder. Telmore må dele mine oplysninger med sociale medier med henblik på markedsføring målrettet mig og andre, og indsamle oplysninger om min interaktion med kommunikationen via tracking pixels. Telmore må spørge mig, om jeg ønsker at udvide mit samtykke.'
  const permissionText3 = 'Læs mere i'
  const permissionText4 = 'Samtykket kan altid'
  const linkText1 = ' Læs mere her'
  const linkText2 = ' Læs mere her'
  const linkText3 = ' privatlivspolitikken'
  const linkText4 = ' tilbagekaldes'
  const telmoreMarkedsfoeringssamtykke = 'https://www.telmore.dk/om-telmore/markedsfoeringssamtykke'
  const telmorePrivatlivspolitik = 'https://www.telmore.dk/om-telmore/privatlivspolitik'
  const telmoreCookieOgPersondata = 'https://www.telmore.dk/om-telmore/cookie-og-persondata'
  const permissionCheckBoxText = 'Ja tak, send mig nyheder, gode råd og tilbud'

  const contactInfo = reactive({
    formValid: false,
    phoneNumber: '',
    email: '',
    emailValid: false
  })

  const privateInfo = reactive({
    valid: true,
    formValid: false,
    name: '',
    street: '',
    houseNumber: '',
    floor: '',
    sideOrDoor: '',
    postalCode: '',
    city: '',
    email: '',
    emailValid: false,
    cpr: '',
    phone: '',
    pickupLocation: null
  })

  const businessInfo = reactive({
    valid: true,
    formValid: false,
    billingAddress: {},
    cvr: '',
    cvrValid: false,
    email: '',
    emailValid: false,
    phone: '',
    pickupLocation: null
  })

  const menu = ref(CUSTOMER_TYPE.PRIVATE)

  // MIO-29627: temporarily hide Erhvervskunde when cart has devices/hardware (scam mitigation)
  const menuItems = computed(() => {
    const items = [
      {
        label: 'Privatkunde',
        value: CUSTOMER_TYPE.PRIVATE,
        component: 'tlm-checkout-customer-private-customer',
        context: privateInfo,
        // Auto-open Privatkunde when MitID code is present, or when it is the only fan (hardware cart)
        activatedMenu: (code.value !== '' && code.value !== undefined) || cartHasHardware.value
      }
    ]
    if (!cartHasHardware.value) {
      items.push({
        label: 'Erhvervskunde',
        value: CUSTOMER_TYPE.BUSINESS,
        component: 'tlm-checkout-customer-business',
        context: businessInfo
      })
    }
    return items
  })

  watch(cartHasHardware, (hasHardware) => {
    if (hasHardware && menu.value === CUSTOMER_TYPE.BUSINESS) {
      menu.value = CUSTOMER_TYPE.PRIVATE
    }
  }, { immediate: true })

  const subscriptionSignUpData = computed(() => {
    if (menu.value === CUSTOMER_TYPE.BUSINESS) {
      return {
        contactPhone: businessInfo.phone,
        emailAddress: businessInfo.email,
        cvrNumber: businessInfo.cvr,
        city: businessInfo.billingAddress.postalDistrict,
        cprNumber: null,
        companyName: businessInfo.billingAddress.companyName,
        zipCode: businessInfo.billingAddress.postalCode,
        streetName: businessInfo.billingAddress.streetName,
        houseNumber: businessInfo.billingAddress.houseNumber,
        isCompanyCustomer: true,
        isSimOnly: false
      }
    } else {
      return {
        contactPhone: privateInfo.phone,
        birthDate: null,
        emailAddress: privateInfo.email,
        cvrNumber: null,
        city: privateInfo.city,
        cprNumber: privateInfo.cpr,
        fullName: privateInfo.name,
        zipCode: privateInfo.postalCode,
        streetName: privateInfo.street,
        houseNumber: privateInfo.houseNumber,
        floor: privateInfo.floor,
        isCompanyCustomer: false,
        isSimOnly: false
      }
    }
  })
  const customerDetails = computed(() => {
    if (menu.value === CUSTOMER_TYPE.PRIVATE) {
      return {
        addressDTO: {
          firstName: privateInfo.name.split(' ').slice(0, -1).join(' '),
          lastName: privateInfo.name.split(' ').slice(-1).join(' '),
          streetName: privateInfo.street,
          houseNumber: privateInfo.houseNumber,
          floor: privateInfo.floor,
          side: privateInfo.sideOrDoor,
          postalCode: privateInfo.postalCode,
          postalDistrict: privateInfo.city
        },
        email: privateInfo.email
      }
    } else {
      return {
        cvrNumber: businessInfo.cvr,
        addressDTO: {
          companyName: businessInfo.billingAddress.company,
          streetName: businessInfo.billingAddress.streetName,
          houseNumber: businessInfo.billingAddress.streetNumber,
          floor: businessInfo.billingAddress.floor,
          side: businessInfo.billingAddress.side,
          postalCode: businessInfo.billingAddress.postalCode,
          postalDistrict: businessInfo.billingAddress.city,
          contactNumber: businessInfo.phone
        },
        email: businessInfo.email
      }
    }
  })

  const osmDTO = ref(null)
  const customerType = ref(null)
  return {
    contactInfo,
    customerDetails,
    subscriptionSignUpData,
    permissionCheckBoxText,
    linkText1,
    linkText2,
    linkText3,
    linkText4,
    telmoreMarkedsfoeringssamtykke,
    telmorePrivatlivspolitik,
    telmoreCookieOgPersondata,
    permissionHeading,
    permissionText1,
    permissionText2,
    permissionText3,
    permissionText4,
    menu,
    menuItems,
    customerType,
    privateInfo,
    businessInfo,
    osmDTO,
    permissionsAccepted,
    cartHasHardware
  }
}
